/**
 * Admin console session limits.
 *
 * A session lasts at most 12 hours from sign-in. Cognito enforces it on the
 * admin app client (an 11-hour refresh token, so the last access token it
 * issues, valid for 1 hour, also expires by hour 12), and the console signs
 * the tab out at the same deadline. These client-side limits end it sooner:
 * tokens live in per-tab sessionStorage, so closing the tab ends the session,
 * and a tab left idle signs itself out.
 *
 * Idle and age expiry only clear the expiring tab's tokens. Revoking tokens
 * is reserved for an explicit sign-out, so an untouched tab (including a
 * duplicate holding a copy of the same refresh token) cannot end the session
 * an admin is actively using elsewhere.
 */

export const ADMIN_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export const ADMIN_SESSION_MAX_MS = 12 * 60 * 60 * 1000;

export const IDLE_SIGN_OUT_MESSAGE =
  'You were signed out after 30 minutes of inactivity. Sign in again to continue.';

export const SESSION_LIMIT_SIGN_OUT_MESSAGE =
  'Admin sessions last 12 hours. Sign in again to continue.';

/** Key prefix Amplify uses for Cognito tokens in its key-value storage. */
export const AMPLIFY_TOKEN_KEY_PREFIX = 'CognitoIdentityServiceProvider.';

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
] as const;

/**
 * Removes Amplify's Cognito tokens from `storage` without contacting Cognito.
 * Used at startup on localStorage (an earlier build left 30-day refresh tokens
 * there) and to end an idle or expired tab: a duplicated tab starts with a
 * copy of this tab's sessionStorage, so revoking the shared refresh token
 * would also end the copy the admin may be actively using.
 */
export function clearCognitoTokens(storage: Storage): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(AMPLIFY_TOKEN_KEY_PREFIX)) keys.push(key);
  }
  keys.forEach(key => storage.removeItem(key));
}

export interface IdleWatchOptions {
  readonly timeoutMs: number;
  readonly onIdle: () => void;
}

/**
 * Calls `onIdle` once after `timeoutMs` without user activity and returns a
 * function that stops watching.
 *
 * Elapsed time is measured from the last activity timestamp rather than
 * trusted to a timer, because browsers throttle timers in background tabs and
 * a sleeping laptop fires them late. The check also runs when the tab becomes
 * visible again.
 */
export function watchIdle({ timeoutMs, onIdle }: IdleWatchOptions): () => void {
  let lastActivity = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const markActive = () => {
    lastActivity = Date.now();
  };

  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, markActive));
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  const check = () => {
    if (stopped) return;
    const idleFor = Date.now() - lastActivity;
    if (idleFor >= timeoutMs) {
      stop();
      onIdle();
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(check, timeoutMs - idleFor);
  };

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') check();
  }

  ACTIVITY_EVENTS.forEach(event =>
    window.addEventListener(event, markActive, { passive: true })
  );
  document.addEventListener('visibilitychange', onVisibilityChange);
  timer = setTimeout(check, timeoutMs);

  return stop;
}

export interface DeadlineWatchOptions {
  /** Epoch milliseconds at which the session ends. */
  readonly deadline: number;
  readonly onExpire: () => void;
}

/**
 * Calls `onExpire` once at `deadline` and returns a function that stops
 * watching. Like {@link watchIdle}, it compares against the clock rather than
 * trusting a throttled background timer, and re-checks when the tab becomes
 * visible again.
 */
export function watchDeadline({ deadline, onExpire }: DeadlineWatchOptions): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  const check = () => {
    if (stopped) return;
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      stop();
      onExpire();
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(check, remaining);
  };

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') check();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  check();

  return stop;
}

/**
 * The session deadline: 12 hours after the original sign-in. Cognito keeps
 * `auth_time` (epoch seconds) unchanged across token refreshes; without it the
 * deadline counts from `now`.
 */
export function sessionDeadline(authTimeSeconds: unknown, now: number = Date.now()): number {
  const signedInAt =
    typeof authTimeSeconds === 'number' && Number.isFinite(authTimeSeconds)
      ? authTimeSeconds * 1000
      : now;
  return signedInAt + ADMIN_SESSION_MAX_MS;
}
